"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import {
  User,
  Heart,
  FileText,
  ChevronDown,
  Globe,
  Package,
  Zap,
  Plug,
  Settings,
  Battery,
  Thermometer,
  Home,
  Phone,
  Info,
  Briefcase,
  Building2,
  Newspaper,
  MessageSquare,
  Cpu,
  GraduationCap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

// Type for grouped categories from API
interface GroupedCategory {
  name: string;
  slug: string;
  subcategories: Array<{
    id: number;
    name: string;
    slug: string;
    productCount: number;
  }>;
  totalProducts: number;
  icon: string;
}

// Map icon names to Lucide components
const iconMap: Record<string, any> = {
  Cpu,
  Plug,
  Battery,
  Zap,
  Package,
  Settings,
  Power: Battery,
  Thermometer,
};

export function MobileNavigation(): JSX.Element {
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [categories, setCategories] = useState<GroupedCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  // Fetch categories from API on mount (use megaMenu=true for simplified navigation)
  useEffect(() => {
    fetch("/api/categories?megaMenu=true")
      .then((res) => res.json())
      .then((data) => {
        setCategories(data);
        setCategoriesLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching categories:", err);
        setCategoriesLoading(false);
      });
  }, []);

  return (
    <div className="lg:hidden bg-[#252626] absolute top-full left-0 w-full border-t border-slate-700 shadow-2xl max-h-[80vh] overflow-y-auto">
      <div className="container mx-auto px-4 py-6">
        {/* Utility Links */}
        <div className="mb-6 pb-4 border-b border-slate-700">
          <div className="flex flex-wrap gap-3 text-sm">
            <Link
              href="/"
              className="text-slate-300 hover:text-[#518BC4] transition-colors"
            >
              Home
            </Link>
            <Link
              href="/services"
              className="text-slate-300 hover:text-[#518BC4] transition-colors"
            >
              Services
            </Link>
            <Link
              href="/industries"
              className="text-slate-300 hover:text-[#518BC4] transition-colors"
            >
              Industries
            </Link>
            <Link
              href="/manufacturers"
              className="text-slate-300 hover:text-[#518BC4] transition-colors"
            >
              Manufacturers
            </Link>
            <Link
              href="/about"
              className="text-slate-300 hover:text-[#518BC4] transition-colors"
            >
              About
            </Link>
            <Link
              href="/careers"
              className="text-slate-300 hover:text-[#518BC4] transition-colors"
            >
              Careers
            </Link>
            <Link
              href="/contact"
              className="text-slate-300 hover:text-[#518BC4] transition-colors"
            >
              Contact
            </Link>
            <Link
              href="/support"
              className="text-slate-300 hover:text-[#518BC4] transition-colors"
            >
              Help
            </Link>
            <Link
              href="/quote/track"
              className="text-slate-300 hover:text-[#518BC4] transition-colors"
            >
              Track Quote
            </Link>
          </div>
        </div>

        {/* Product Categories */}
        <div className="mb-6">
          <h3 className="text-white font-semibold mb-3 text-sm uppercase tracking-wide">
            Product Categories
          </h3>
          {categoriesLoading ? (
            <div className="text-slate-400 text-sm">Loading categories...</div>
          ) : (
            <div className="space-y-2">
              {categories.map((category) => {
                const IconComponent = iconMap[category.icon] || Package;
                return (
                  <Collapsible
                    key={category.name}
                    open={expandedCategory === category.name}
                    onOpenChange={(open) =>
                      setExpandedCategory(open ? category.name : null)
                    }
                  >
                    <CollapsibleTrigger asChild>
                      <Button
                        variant="ghost"
                        className="w-full justify-between h-auto p-3 text-left text-slate-200 hover:bg-slate-700 hover:text-[#518BC4] rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <IconComponent className="w-5 h-5 text-[#518BC4]" />
                          <span className="font-medium">{category.name}</span>
                        </div>
                        <ChevronDown
                          className={`w-4 h-4 transition-transform ${
                            expandedCategory === category.name
                              ? "rotate-180"
                              : ""
                          }`}
                        />
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="pl-8 pt-2 pb-2 space-y-1">
                      <Link
                        href={`/products?category=${category.slug}&page=1`}
                        className="block py-2 px-3 text-sm text-slate-300 hover:text-[#518BC4] hover:bg-slate-700 rounded-md transition-colors"
                      >
                        View All
                      </Link>
                      {category.subcategories.map((subcategory) => (
                        <Link
                          key={subcategory.id}
                          href={`/products?category=${subcategory.slug}&page=1`}
                          className="block py-2 px-3 text-sm text-slate-400 hover:text-[#518BC4] hover:bg-slate-700 rounded-md transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            <span>{subcategory.name}</span>
                            <span className="text-xs text-slate-500">
                              {subcategory.productCount.toLocaleString()}
                            </span>
                          </div>
                        </Link>
                      ))}
                    </CollapsibleContent>
                  </Collapsible>
                );
              })}
            </div>
          )}
        </div>

        {/* Main Navigation */}
        <div className="mb-6">
          <h3 className="text-white font-semibold mb-3 text-sm uppercase tracking-wide">
            Navigation
          </h3>
          <nav className="space-y-2">
            <Link
              href="/"
              className="flex items-center gap-3 px-3 py-3 text-slate-200 hover:bg-slate-700 hover:text-[#518BC4] rounded-lg transition-colors"
            >
              <Home className="w-5 h-5" />
              <span>Home</span>
            </Link>
            <Link
              href="/products"
              className="flex items-center gap-3 px-3 py-3 text-slate-200 hover:bg-slate-700 hover:text-[#518BC4] rounded-lg transition-colors"
            >
              <Package className="w-5 h-5" />
              <span>All Products</span>
            </Link>
            <Link
              href="/manufacturers"
              className="flex items-center gap-3 px-3 py-3 text-slate-200 hover:bg-slate-700 hover:text-[#518BC4] rounded-lg transition-colors"
            >
              <Building2 className="w-5 h-5" />
              <span>Manufacturers</span>
            </Link>
            <Link
              href="/industries"
              className="flex items-center gap-3 px-3 py-3 text-slate-200 hover:bg-slate-700 hover:text-[#518BC4] rounded-lg transition-colors"
            >
              <Briefcase className="w-5 h-5" />
              <span>Industries</span>
            </Link>
            {/* <Link
              href="/case-studies"
              className="flex items-center gap-3 px-3 py-3 text-slate-200 hover:bg-slate-700 hover:text-[#518BC4] rounded-lg transition-colors"
            >
              <Newspaper className="w-5 h-5" />
              <span>Case Studies</span>
            </Link> */}
            <Link
              href="/about"
              className="flex items-center gap-3 px-3 py-3 text-slate-200 hover:bg-slate-700 hover:text-[#518BC4] rounded-lg transition-colors"
            >
              <Info className="w-5 h-5" />
              <span>About Us</span>
            </Link>
            <Link
              href="/careers"
              className="flex items-center gap-3 px-3 py-3 text-slate-200 hover:bg-slate-700 hover:text-[#518BC4] rounded-lg transition-colors"
            >
              <GraduationCap className="w-5 h-5" />
              <span>Careers</span>
            </Link>
            <Link
              href="/contact"
              className="flex items-center gap-3 px-3 py-3 text-slate-200 hover:bg-slate-700 hover:text-[#518BC4] rounded-lg transition-colors"
            >
              <MessageSquare className="w-5 h-5" />
              <span>Contact</span>
            </Link>
          </nav>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          {/* <Button asChild variant="outline" className="w-full justify-start h-12 bg-transparent border-slate-600 text-slate-200 hover:bg-slate-700 hover:text-white">
            <Link href="/account">
              <User className="w-5 h-5 mr-3" />
              My Account
            </Link>
          </Button> */}

          <Button
            asChild
            variant="outline"
            className="w-full justify-start h-12 bg-transparent border-slate-600 text-slate-200 hover:bg-slate-700 hover:text-white"
          >
            <Link href="/wishlist">
              <Heart className="w-5 h-5 mr-3" />
              Wishlist
            </Link>
          </Button>
          <Button
            asChild
            variant="outline"
            className="w-full justify-start h-12 bg-transparent border-slate-600 text-slate-200 hover:bg-slate-700 hover:text-white"
          >
            <Link href="/quote">
              <FileText className="w-5 h-5" />
              Request Quote
            </Link>
          </Button>
        </div>

        {/* Language & Auth */}
        <div className="mt-6 pt-4 border-t border-slate-700 space-y-3">
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-300 hover:bg-slate-700 hover:text-white"
          >
            <Globe className="w-5 h-5 mr-3" />
            Language: English
            <ChevronDown className="w-4 h-4 ml-auto" />
          </Button>

          {/* <Link
            href="/auth/login"
            className="block w-full text-center py-3 text-[#518BC4] hover:text-[#7C1414] font-medium transition-colors"
          >
            Sign In / Register
          </Link> */}
        </div>
      </div>
    </div>
  );
}
